/**
 * ============================================================================
 * TREND & Δ WoW CELLS — AG Grid renderers for the executive-summary trend columns.
 * ============================================================================
 *
 * TrendCell: InlineTrend sparkline of the row's weekly values (historical +
 * current; forward weeks excluded). Percentage rows show the goal line and
 * percent formatting; volume/count rows use compact number formatting.
 * Receives `weeks` through cellRendererParams from MetricTableSection.
 *
 * MetricDelta: current vs previous week via DeltaChip, direction-aware
 * (goalDirection LTE = lower is better). Missing weeks render "–", never a
 * misleading number (spec R2.3). Rendered inside the pinned metric cell rather
 * than in its own column, so it costs no horizontal space.
 */

import React from 'react';
import { ICellRendererParams } from 'ag-grid-community';
import InlineTrend from 'src/components/dashboard/InlineTrend';
import DeltaChip from 'src/components/dashboard/DeltaChip';
import { MetricTableRow, WeekColumn, selectMetricTrend } from 'src/features/api/tableSelectors';

interface TrendCellParams extends ICellRendererParams {
  weeks?: WeekColumn[];
}

const RATE_COLOR = '#0972d3'; // Cloudscape blue-600
const VOLUME_COLOR = '#5f6b7a'; // Cloudscape grey-600

function formatCompact(v: number): string {
  if (Math.abs(v) >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString();
}

export const TrendCell: React.FC<TrendCellParams> = ({ data, weeks }) => {
  const row = data as MetricTableRow | undefined;
  if (!row || !weeks || weeks.length === 0) return <span>–</span>;

  const trend = selectMetricTrend(row, weeks);
  if (trend.values.length === 0) return <span>–</span>;

  return (
    <InlineTrend
      values={trend.values}
      goal={row.isRate && row.goal > 0 ? row.goal : undefined}
      color={row.isRate ? RATE_COLOR : VOLUME_COLOR}
      formatValue={row.isRate ? undefined : formatCompact}
      weekLabels={trend.weekLabels}
      selectedIndex={trend.values.length - 1}
    />
  );
};

/** Δ WoW indicator for one row, for use inside the pinned metric cell. */
export const MetricDelta: React.FC<{ row: MetricTableRow; weeks: WeekColumn[] }> = ({
  row,
  weeks,
}) => {
  if (weeks.length === 0) return <span>–</span>;

  const { current, previous } = selectMetricTrend(row, weeks);
  if (current === null || previous === null) return <span>–</span>;

  return (
    <DeltaChip
      current={current}
      previous={previous}
      unit={row.isRate ? 'pp' : 'volume'}
      // Catalog goalDirection is GTE | LTE (metrics-contract-adoption R1.3).
      lowerIsBetter={row.goalDirection === 'LTE'}
    />
  );
};
